// composables/useEpiFetch.ts
import type { AuthenticationResult } from '@azure/msal-browser'

export type ApiErrorResponse = {
  title?: string
  status?: number
  detail?: string
  errors?: Record<string, string[]> | string[]
  message?: string
}

type Options = {
  headers?: Record<string, string>
  body?: Record<string, any>
}

const useEpiFetch = async (environment: any, endpoint: string, options: Options = {}, versionOverride?: string) => {
  try {
    const { acquireTokenSilent } = useMsal(environment.msal)
    const msalResponse = (await acquireTokenSilent()) as AuthenticationResult
    const { accessToken } = msalResponse

    const headers = {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      ...options.headers
    }
    const apiVersion = versionOverride ?? environment.apiVersion
    const fullPath = `${environment.apiUrl}/api/${apiVersion}/${endpoint}`

    const response = await fetch(fullPath, {
      ...options,
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined
    })

    if (!response.ok) {
      let errorBody: ApiErrorResponse
      try {
        errorBody = await response.json()
      } catch {
        // Response body was not valid JSON — build a structured error from the HTTP status
        errorBody = { title: response.statusText, status: response.status }
      }
      return { data: null, error: errorBody }
    }

    const data = await response.json()

    return { data, error: null }
  } catch (error: any) {
    // Network failures, MSAL errors, etc. — normalise into the same shape
    const normalised: ApiErrorResponse = {
      title: error?.name ?? 'Error',
      detail: error?.message ?? 'An unexpected error occurred.',
      status: 0
    }
    return { data: null, error: normalised }
  }
}

export default useEpiFetch
